import { useCallback, useState } from 'react'
import { useDialog } from '../../shared/hooks/useDialog'
import { extractErrorMessage } from '../../../helpers/apiClient.helper'
import { flushToPaint } from '../helpers/flushToPaint.helper'
import { useAccountAdminCopy } from './useAccountAdminCopy'

export type MemberPendingAction = 'remove' | 'promote' | 'demote'

export interface MemberActionPending {
  userId: string
  action: MemberPendingAction
}

interface UseAccountAdminDialogsParams {
  removeMember: (userId: string) => Promise<unknown>
  patchMemberRole: (userId: string, role: 'ADMIN' | 'MEMBER') => Promise<unknown>
  leaveOrganization: () => Promise<unknown>
}

export function useAccountAdminDialogs({
  removeMember,
  patchMemberRole,
  leaveOrganization,
}: UseAccountAdminDialogsParams) {
  const copy = useAccountAdminCopy()
  const { showDialog, showAlert } = useDialog()
  const [memberLeaveBusy, setMemberLeaveBusy] = useState(false)
  const [memberActionPending, setMemberActionPending] = useState<MemberActionPending | null>(null)

  const handleMemberLeaveOrganization = useCallback(async () => {
    const ok = await showDialog({
      title: copy.LEAVE_ORG_TITLE,
      message: copy.LEAVE_ORG_MESSAGE,
      type: 'danger',
      confirmText: copy.LEAVE_ORG_CONFIRM,
      cancelText: 'Cancel',
    })
    if (!ok) return
    setMemberLeaveBusy(true)
    try {
      await leaveOrganization()
      await showAlert({
        title: copy.LEAVE_ORG_DONE_TITLE,
        message: copy.LEAVE_ORG_DONE_MESSAGE,
        type: 'default',
      })
    } catch (e) {
      await showAlert({ title: 'Error', message: extractErrorMessage(e, 'Could not leave'), type: 'danger' })
    } finally {
      setMemberLeaveBusy(false)
    }
  }, [copy, leaveOrganization, showAlert, showDialog])

  const handleRemoveMember = useCallback(
    async (memberUserId: string) => {
      const ok = await showDialog({
        title: copy.REMOVE_MEMBER_TITLE,
        message: copy.REMOVE_MEMBER_MESSAGE,
        type: 'danger',
        confirmText: copy.REMOVE_MEMBER,
        cancelText: 'Cancel',
      })
      if (!ok) return
      await flushToPaint(() => setMemberActionPending({ userId: memberUserId, action: 'remove' }))
      try {
        await removeMember(memberUserId)
      } catch (e) {
        await showAlert({ title: 'Error', message: extractErrorMessage(e, 'Remove failed'), type: 'danger' })
      } finally {
        setMemberActionPending(null)
      }
    },
    [copy, removeMember, showAlert, showDialog],
  )

  const handlePromoteToAdmin = useCallback(
    async (memberUserId: string) => {
      const ok = await showDialog({
        title: copy.MAKE_ADMIN_TITLE,
        message: copy.MAKE_ADMIN_MESSAGE,
        type: 'default',
        confirmText: copy.MAKE_ADMIN,
        cancelText: 'Cancel',
      })
      if (!ok) return
      await flushToPaint(() => setMemberActionPending({ userId: memberUserId, action: 'promote' }))
      try {
        await patchMemberRole(memberUserId, 'ADMIN')
      } catch (e) {
        await showAlert({ title: 'Error', message: extractErrorMessage(e, 'Could not update role'), type: 'danger' })
      } finally {
        setMemberActionPending(null)
      }
    },
    [copy, patchMemberRole, showAlert, showDialog],
  )

  const handleDemoteFromAdmin = useCallback(
    async (memberUserId: string) => {
      const ok = await showDialog({
        title: copy.DEMOTE_ADMIN_TITLE,
        message: copy.DEMOTE_ADMIN_MESSAGE,
        type: 'danger',
        confirmText: copy.DEMOTE_ADMIN,
        cancelText: 'Cancel',
      })
      if (!ok) return
      await flushToPaint(() => setMemberActionPending({ userId: memberUserId, action: 'demote' }))
      try {
        await patchMemberRole(memberUserId, 'MEMBER')
      } catch (e) {
        await showAlert({ title: 'Error', message: extractErrorMessage(e, 'Could not update role'), type: 'danger' })
      } finally {
        setMemberActionPending(null)
      }
    },
    [copy, patchMemberRole, showAlert, showDialog],
  )

  return {
    memberLeaveBusy,
    memberActionPending,
    handleMemberLeaveOrganization,
    handleRemoveMember,
    handlePromoteToAdmin,
    handleDemoteFromAdmin,
  }
}
